import { useState } from "react";
import {
  Title, Group, Button, TextInput, Select, Table, ActionIcon, Stack, Text, Card, Badge, Menu,
} from "@mantine/core";
import { IconPlus, IconSearch, IconDots, IconPencil, IconTrash, IconHistory, IconEye } from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { type Ereignis, EreignisStatus, type Locale } from "@iso27001/shared";
import { ereignisseApi } from "../../api/ereignisse.js";
import { EreignisFormModal } from "./EreignisFormModal.js";
import { AuditHistoryModal } from "../../components/AuditHistoryModal.js";
import { useAuth } from "../../auth/AuthContext.js";
import { formatDate } from "../../i18n/format-date.js";
import { useEnumLabel } from "../../i18n/enum-label.js";
import { useRecordDialog } from "../../components/use-record-dialog.js";
import { rowOpenProps, stopRowOpen, cellBadgeStyles, nowrap } from "../../components/list-table.js";
import { isEreignisClosed } from "../../components/record-state.js";
import { ereignisStatusColor } from "../../components/status-colors.js";
import { classBadges } from "./classification.js";

export function EreignisseListPage() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "editor";
  const qc = useQueryClient();
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const { label } = useEnumLabel();

  const statusFilterOptions = [
    { value: "", label: t("common.filter.allStatus") },
    ...EreignisStatus.options.map((value) => ({ value, label: label("ereignisStatus", value) })),
  ];

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [auditId, setAuditId] = useState<number | null>(null);
  const dialog = useRecordDialog<Ereignis>();

  const list = useQuery({
    queryKey: ["ereignisse", { q, status: statusFilter }],
    queryFn: () => ereignisseApi.list({
      q: q || undefined,
      status: (statusFilter || undefined) as EreignisStatus | undefined,
      pageSize: 200,
    }),
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => ereignisseApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ereignisse"] });
      notifications.show({ message: t("ereignisse.list.notify.deleted"), color: "teal" });
    },
  });

  const onDelete = (e: Ereignis) => {
    modals.openConfirmModal({
      title: t("ereignisse.list.confirmDelete.title"),
      children: <Text size="sm">{t("ereignisse.list.confirmDelete.message", { code: e.code })}</Text>,
      labels: { confirm: t("common.action.delete"), cancel: t("common.action.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => removeMut.mutate(e.id),
    });
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>{t("ereignisse.list.title")}</Title>
        {canEdit && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => dialog.open(null)}>
            {t("ereignisse.newEvent")}
          </Button>
        )}
      </Group>
      <Card withBorder padding="xs">
        <Group>
          <TextInput
            placeholder={t("common.search.placeholder")}
            leftSection={<IconSearch size={14} />}
            value={q}
            onChange={(ev) => setQ(ev.currentTarget.value)}
            style={{ flex: 1, maxWidth: 320 }}
          />
          <Select
            data={statusFilterOptions}
            value={statusFilter}
            onChange={(val) => setStatusFilter(val ?? "")}
            style={{ width: 180 }}
            placeholder={t("common.filter.allStatus")}
          />
          <Text size="sm" c="dimmed">{list.data ? t("common.entriesCount", { count: list.data.total }) : ""}</Text>
        </Group>
      </Card>
      <Card withBorder padding={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={nowrap}>{t("common.field.code")}</Table.Th>
              <Table.Th style={nowrap}>{t("common.field.date")}</Table.Th>
              <Table.Th>{t("common.field.description")}</Table.Th>
              <Table.Th>{t("ereignisse.classification.label")}</Table.Th>
              <Table.Th style={nowrap}>{t("common.field.status")}</Table.Th>
              <Table.Th style={{ width: 48 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {list.data?.items.map((e) => (
              <Table.Tr key={e.id} {...rowOpenProps(() => dialog.open(e))}>
                <Table.Td><Text ff="monospace" size="sm" style={nowrap}>{e.code}</Text></Table.Td>
                <Table.Td>
                  <Text size="sm" style={nowrap}>
                    {formatDate(e.reportedAt, locale)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" lineClamp={1}>{e.description}</Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    {classBadges(e, t).map((b) => (
                      <Badge key={b.label} color={b.color} variant="light" size="xs" styles={cellBadgeStyles}>{b.label}</Badge>
                    ))}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Badge color={ereignisStatusColor[e.status]} variant="light" styles={cellBadgeStyles}>{label("ereignisStatus", e.status)}</Badge>
                </Table.Td>
                <Table.Td {...stopRowOpen}>
                  <Menu position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="subtle" aria-label={t("common.action.actions")}><IconDots size={16} /></ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {canEdit && !isEreignisClosed(e.status) ? (
                        <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => dialog.open(e)}>
                          {t("common.action.edit")}
                        </Menu.Item>
                      ) : (
                        <Menu.Item leftSection={<IconEye size={14} />} onClick={() => dialog.open(e)}>
                          {t("common.action.open")}
                        </Menu.Item>
                      )}
                      <Menu.Item leftSection={<IconHistory size={14} />} onClick={() => setAuditId(e.id)}>
                        {t("common.action.history")}
                      </Menu.Item>
                      {canEdit && (
                        <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => onDelete(e)}>
                          {t("common.action.delete")}
                        </Menu.Item>
                      )}
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
            {list.data?.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text ta="center" c="dimmed" py="md">{t("ereignisse.list.empty")}</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>
      <EreignisFormModal key={dialog.key} opened={dialog.opened} onClose={dialog.close} initial={dialog.record} />
      <AuditHistoryModal
        opened={auditId !== null}
        onClose={() => setAuditId(null)}
        queryKey={["ereignisse", "audit", auditId]}
        fetchFn={() => ereignisseApi.audit(auditId!)}
      />
    </Stack>
  );
}
